import classNames from 'classnames';
import React, { useCallback } from 'react';
import Checkbox from '../../../../../ui/components/Checkbox';
import { Cell, CellProps } from '../../../../../ui/components/Table';
import { useBEM } from '../../../../../ui/utils/hooks';
import './CheckboxCell.less';

interface Props extends CellProps {
  id: string;
  className?: string;
  onChange: any;
  isSelected: boolean;
  hideCheckbox?: boolean;
  disabled?: boolean;
  cellType?: string;
}

export const CheckboxCell: React.FC<Props> = ({
  children,
  className,
  onChange,
  isSelected,
  hideCheckbox,
  cellType,
  disabled,
  ...props
}) => {
  const bemClasses = useBEM('CheckboxCell');

  // Handle clicks in the capture phase to prevent checkbox toggle on modifier keys
  const handleClickCapture = useCallback((e: React.MouseEvent) => {
    const hasModifierKey = e.shiftKey || e.ctrlKey || e.metaKey;

    if (hasModifierKey) {
      // Prevent the checkbox from toggling when modifier keys are pressed
      // This allows shift-click range selection and ctrl/cmd-click additive selection
      e.preventDefault();
      // Don't stop propagation - let it bubble to the row handler
    }
  }, []);

  // Handle clicks in the bubble phase to stop propagation for normal clicks
  const handleClick = useCallback((e: React.MouseEvent) => {
    const hasModifierKey = e.shiftKey || e.ctrlKey || e.metaKey;

    if (!hasModifierKey) {
      // Normal click - stop propagation so it doesn't trigger row handler
      e.stopPropagation();
    }
    // For modifier key clicks, allow propagation to row handler
  }, []);

  return (
    <Cell
      className={classNames(bemClasses({ className }), 'minimize')}
      {...props}
      ref={null}
      onClickCapture={handleClickCapture}
      onClick={handleClick}
    >
      <Checkbox
        id={props.id}
        className={bemClasses({
          element: 'checkbox',
          modifiers: [hideCheckbox ? '' : 'active'],
        })}
        checked={!!isSelected}
        onChange={onChange}
        stopPropagation={false}
        hitBox="default"
        dispatchEventToWindow={true}
        disabled={disabled}
      ></Checkbox>
    </Cell>
  );
};
